<template>
  <!-- Header needs to blend in with the homepage on XS2Event branded shops -->
  <div :class="['nav-header  w-full z-30 ', { 'bg-header shadow': !isXS2EventShop || route.name != 'home' || !lg }]">
    <Disclosure id="nav" ref="disclosure" as="nav" class="lg:h-auto page-container">
      <div class="py-6 grid gap-6">
        <!-- First Row: Logo on the Left, Right Elements on the Right -->
        <div class="relative flex justify-between items-center gap-3">
          <div class="flex items-center gap-3">
            <!-- Mobile Menu Button (Hamburger) -->
            <div class="inset-y-0 left-0 flex items-center lg:hidden">
              <DisclosureButton
                class="inline-flex items-center justify-center outline-none rounded-md text-menu-link"
                @click="isMenuOpen = true">
                <span class="sr-only">{{ $t("Open main menu") }}</span>
                <Bars3Icon class="block h-6 w-6" aria-label="Menu" aria-hidden="true" />
              </DisclosureButton>
            </div>

            <!-- Mobile Logo -->
            <div class="flex lg:hidden">
              <RouterLink to="/"><img class="mobile-logo" alt="logo" :src="logo_mobile" /></RouterLink>
            </div>

            <!-- Desktop Logo -->
            <div class="hidden lg:flex flex-shrink-0 items-center">
              <RouterLink to="/"><img class="logo max-h-8" :src="logo" alt="logo" /></RouterLink>
            </div>

            <SearchMenu class="hidden ml-5 lg:block w-180" />
          </div>

          <Alert v-if="cart.allocationId" status="warning">
            {{ $t("Please beware! Regular bookings are disabled during your allocation checkout.") }}
            <button class="underline" @click="cart.emptyCart()">{{ $t("Clear cart") }}</button>
          </Alert>

          <!-- Right Elements -->
          <div class="flex items-center gap-4">
            <BrandMenu v-if="isLocal || (isTest && (isXS2Event || brandOverride))" @click="isMenuOpen = false" />
            <UserMenu @click="isMenuOpen = false" @open-impersonation="isImpersonationOpen = true" />
            <CartMenu v-if="!isB2C" data-testid="shopping-cart-menu" />
          </div>
        </div>

        <!-- Second Row: Left Elements (Main Menu) -->
        <div class="hidden lg:flex justify-start items-center mt-2 lg:mt-0">
          <MainMenu />
          <CustomerSupportMenu />
        </div>
      </div>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="-translate-y-4 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="-translate-y-4 opacity-0">
        <DisclosurePanel v-if="isMenuOpen" static class="lg:hidden z-20">
          <MainMenu :open="isMenuOpen" @close="isMenuOpen = false" />
        </DisclosurePanel>
      </transition>
    </Disclosure>
  </div>

  <!-- Mobile Search Bar -->
  <div class="flex lg:hidden border-b bg-header shadow border-solid border-white w-full page-container pb-3">
    <SearchMenu class="w-full" />
  </div>

  <XS2EventLogoBackground v-if="isXS2EventShop && route.name == 'home'" />
</template>

<script setup lang="ts">
import { SearchMenu, UserMenu, CartMenu, CustomerSupportMenu } from "./menu";
import MainMenu from "./menu/MainMenu.vue";
import { ref, watch } from "vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { Bars3Icon } from "@heroicons/vue/24/outline";
import brandconfig, { isB2C, isXS2EventShop } from "@/brandconfig";
import { useCartStore } from "@/store/cart";
import BrandMenu from "./menu/BrandMenu.vue";
import { useRoute } from "vue-router";
import { isLocal, isTest } from "@/config/index.js";
import { Alert } from "@xs2event/js-lib/components";
import { useUserStore } from "@/store/user.js";
import XS2EventLogoBackground from "./XS2EventLogoBackground.vue";
import { useBreakpoints } from "@xs2event/js-lib/util";
import { useLocalStorage } from "@vueuse/core";

const userStore = useUserStore();

const { lg } = useBreakpoints();

const isMenuOpen = ref(false);

const route = useRoute();

watch(route, () => (isMenuOpen.value = false));

const cart = useCartStore();

const isImpersonationOpen = ref(false);

const brandOverride = useLocalStorage("b2c_override");

const logo = brandconfig.LOGO;
const logo_mobile = brandconfig.LOGO_MOBILE;

const isXS2Event = ["9fd5d80db8c548208f1abd44c9b6c5e4_dst", "9055d412ef94473b9ae16c7b15db6141_dst"].includes(
  userStore.distributor_id
);
</script>

<style scoped>
.logo {
  height: 30px;
}
</style>
